import { STAGE_DURATIONS } from '@/domain/lifecycle';
import type { CareTier } from '@/domain/care/evolution';
import type { Creature, CreatureState, Stage } from '@/domain/creature/types';
import { ALL_BODY_GENES, type AlchemyType, type BodyGene } from '@/domain/genetics/types';
import { useStore } from '@/infra/store';
import { getCombatRunService } from '@/services/combat-run';
import { composeEnemyPopulation } from '@/domain/combat/enemy-composition';
import { boundsOfShape, type Point } from '@/domain/world/geometry';
import { CURRENT_WORLD_MAP } from '@/domain/world/regions/current-world';
import type { LocomotionState, WorldFacing } from '@/domain/world/actor-presentation';
import type { ActorVisualAction } from '@/runtime/render/actor-action-state';

export type RegionalDebugOverlay = 'collision' | 'zones' | 'entrances' | 'spawns' | 'camera';
export const REGIONAL_DEBUG_OVERLAYS: readonly RegionalDebugOverlay[] = ['collision', 'zones', 'entrances', 'spawns', 'camera'];
const enabledRegionalOverlays = new Set<RegionalDebugOverlay>();

export function toggleRegionalDebugOverlay(overlay: RegionalDebugOverlay): boolean {
  if (enabledRegionalOverlays.has(overlay)) enabledRegionalOverlays.delete(overlay);
  else enabledRegionalOverlays.add(overlay);
  return enabledRegionalOverlays.has(overlay);
}

export function activeRegionalDebugOverlays(): ReadonlySet<RegionalDebugOverlay> {
  return new Set(enabledRegionalOverlays);
}

export const WORLD_DEBUG_TARGETS: Readonly<Record<string, Point>> = {
  'entry-meadow': { x: 180, y: 800 }, 'entry-forest': { x: 1472, y: 160 },
  'entry-cave': { x: 192, y: 1120 }, 'entry-coast': { x: 1472, y: 1120 },
  'arena-open': { x: 720, y: 250 }, 'arena-sturdy': { x: 250, y: 270 },
};

export type WorldVisualTestKind = 'entrance' | 'arena' | 'body' | 'stage' | 'presentation' | 'action' | 'footprint' | 'overlap';
export interface WorldVisualTestCase {
  id: string;
  label: string;
  kind: WorldVisualTestKind;
  target: Point;
  body?: BodyGene;
  stage?: Exclude<Stage, 'dead'>;
  facing?: WorldFacing;
  locomotion?: LocomotionState;
  action?: ActorVisualAction;
}

export const ACTOR_FACING_CASES: readonly WorldFacing[] = ['down', 'left', 'right', 'up'];
export const ACTOR_ACTION_CASES: readonly ActorVisualAction[] = [
  'idle', 'walk', 'dodge', 'attack', 'skill', 'hurt', 'defeated',
];

const visualHome = { x: 650, y: 520 } as const;
const centerOf = (area: Parameters<typeof boundsOfShape>[0]): Point => {
  const bounds = boundsOfShape(area);
  return { x: bounds.x + bounds.w / 2, y: bounds.y + bounds.h / 2 };
};

export const WORLD_VISUAL_TEST_CASES: readonly WorldVisualTestCase[] = [
  ...CURRENT_WORLD_MAP.regions.flatMap((region) => region.entrances.map((entrance) => ({
    id: `entrance-${entrance.id}`, label: `Entrada: ${entrance.id}`, kind: 'entrance' as const,
    target: centerOf(entrance.area),
  }))),
  ...CURRENT_WORLD_MAP.regions.flatMap((region) => region.zones.filter(({ kind }) => kind === 'arena').map((zone) => ({
    id: `arena-${zone.id}`, label: `Arena: ${zone.id}`, kind: 'arena' as const, target: centerOf(zone.area),
  }))),
  ...ALL_BODY_GENES.map((body) => ({ id: `body-${body}`, label: `Corpo: ${body}`, kind: 'body' as const, target: visualHome, body })),
  ...(['egg', 'baby', 'teen', 'adult', 'elder'] as const).map((stage) => ({ id: `stage-${stage}`, label: `Fase: ${stage}`, kind: 'stage' as const, target: visualHome, stage })),
  ...ACTOR_FACING_CASES.flatMap((facing) => (['idle', 'walk'] as const).map((locomotion) => ({
    id: `presentation-${facing}-${locomotion}`, label: `${facing} / ${locomotion}`,
    kind: 'presentation' as const, target: visualHome, facing, locomotion,
  }))),
  ...ACTOR_FACING_CASES.flatMap((facing) => ACTOR_ACTION_CASES.map((action) => ({
    id: `action-${facing}-${action}`, label: `Ação: ${facing} / ${action}`,
    kind: 'action' as const, target: visualHome, facing, action,
  }))),
  ...ALL_BODY_GENES.map((body) => ({ id: `footprint-${body}`, label: `Hitbox: ${body}`, kind: 'footprint' as const, target: { x: 280, y: 315 }, body })),
  { id: 'overlap-behind', label: 'Profundidade: atrás', kind: 'overlap', target: { x: 250, y: 260 } },
  { id: 'overlap-front', label: 'Profundidade: frente', kind: 'overlap', target: { x: 250, y: 325 } },
] as const;

type WorldVisualOverride = {
  facing?: WorldFacing | undefined;
  locomotion?: LocomotionState | undefined;
  action?: ActorVisualAction | undefined;
};
let visualOverride: WorldVisualOverride | null = null;

export function activeWorldVisualOverride(): Readonly<WorldVisualOverride> | null {
  return visualOverride ? { ...visualOverride } : null;
}

export function teleportToWorldDebugTarget(id: string): boolean {
  const target = WORLD_DEBUG_TARGETS[id];
  if (!target || import.meta.env.PROD) return false;
  useStore.getState().setPlayerPos(target.x, target.y);
  useStore.getState().setInWorld(true);
  return true;
}

export type TestPresetCategory = 'stage' | 'state' | 'flow';

export type TestPresetId =
  | 'stage-egg'
  | 'stage-baby'
  | 'stage-teen'
  | 'stage-adult'
  | 'stage-elder'
  | 'state-perfect'
  | 'state-happy'
  | 'state-sad'
  | 'state-hungry'
  | 'state-dirty'
  | 'state-sick'
  | 'state-tantrum'
  | 'state-exhausted'
  | 'state-critical'
  | 'flow-breeding'
  | 'flow-expedition'
  | 'flow-evolution-good'
  | 'flow-evolution-medium'
  | 'flow-evolution-bad'
  | 'flow-evolution-secret'
  | 'flow-memorial';

export interface TestPresetDefinition {
  id: TestPresetId;
  category: TestPresetCategory;
  label: string;
}

export type CombatTestScenarioId =
  | 'combat-fire' | 'combat-water' | 'combat-earth' | 'combat-air' | 'combat-life' | 'combat-shadow'
  | 'combat-dodge' | 'combat-fauna' | 'combat-wild' | 'combat-dominant';

export const COMBAT_TEST_SCENARIOS: ReadonlyArray<{ id: CombatTestScenarioId; label: string }> = [
  { id: 'combat-fire', label: 'Fogo: cone + queimadura' },
  { id: 'combat-water', label: 'Água: onda + lentidão' },
  { id: 'combat-earth', label: 'Terra: impacto + escudo' },
  { id: 'combat-air', label: 'Ar: explosão + avanço' },
  { id: 'combat-life', label: 'Vida: pulso + cura' },
  { id: 'combat-shadow', label: 'Sombra: avanço + roubo' },
  { id: 'combat-dodge', label: 'Esquiva e invulnerabilidade' },
  { id: 'combat-fauna', label: 'Fauna regional' },
  { id: 'combat-wild', label: 'Himotchi selvagem' },
  { id: 'combat-dominant', label: 'Selvagem dominante' },
] as const;

export const ALL_TEST_PRESETS: readonly TestPresetDefinition[] = [
  { id: 'stage-egg', category: 'stage', label: 'Ovo' },
  { id: 'stage-baby', category: 'stage', label: 'Bebê' },
  { id: 'stage-teen', category: 'stage', label: 'Adolescente' },
  { id: 'stage-adult', category: 'stage', label: 'Adulto' },
  { id: 'stage-elder', category: 'stage', label: 'Idoso' },
  { id: 'state-perfect', category: 'state', label: 'Perfeito' },
  { id: 'state-happy', category: 'state', label: 'Feliz' },
  { id: 'state-sad', category: 'state', label: 'Triste' },
  { id: 'state-hungry', category: 'state', label: 'Faminto' },
  { id: 'state-dirty', category: 'state', label: 'Sujo' },
  { id: 'state-sick', category: 'state', label: 'Doente' },
  { id: 'state-tantrum', category: 'state', label: 'Birra' },
  { id: 'state-exhausted', category: 'state', label: 'Exausto' },
  { id: 'state-critical', category: 'state', label: 'Negligência crítica' },
  { id: 'flow-breeding', category: 'flow', label: 'Cruzamento pronto' },
  { id: 'flow-expedition', category: 'flow', label: 'Expedição pronta' },
  { id: 'flow-evolution-good', category: 'flow', label: 'Evolução boa' },
  { id: 'flow-evolution-medium', category: 'flow', label: 'Evolução média' },
  { id: 'flow-evolution-bad', category: 'flow', label: 'Evolução ruim' },
  { id: 'flow-evolution-secret', category: 'flow', label: 'Evolução secreta' },
  { id: 'flow-memorial', category: 'flow', label: 'Memorial' },
] as const;

export type TestStatPatch = Partial<Pick<CreatureState,
  'hunger' | 'happiness' | 'weight' | 'discipline' | 'poopCount' | 'vitality'
>>;

const clamp = (value: number, min = 0, max = 100): number =>
  Math.min(max, Math.max(min, Math.round(Number.isFinite(value) ? value : min)));

const healthyState = (state: CreatureState, nowMs: number): CreatureState => ({
  ...state,
  hunger: 80,
  happiness: 80,
  weight: 50,
  discipline: 60,
  poopCount: 0,
  lastPoopAt: nowMs,
  dead: false,
  causeOfDeath: null,
  tantrum: false,
  tantrumStartedAt: null,
  attentionLowSince: null,
  sick: false,
  sickSince: null,
  healDoses: 0,
  lastHealAt: null,
  lowHealthSince: null,
  vitality: 90,
});

const ageForStage = (stage: Exclude<Stage, 'dead'>): number => {
  const d = STAGE_DURATIONS;
  if (stage === 'egg') return 0;
  if (stage === 'baby') return d.egg + 1_000;
  if (stage === 'teen') return d.egg + d.baby + 1_000;
  if (stage === 'adult') return d.egg + d.baby + d.teen + 1_000;
  return d.egg + d.baby + d.teen + d.adult + 1_000;
};

function atStage(creature: Creature, stage: Exclude<Stage, 'dead'>, nowMs: number): Creature {
  const adult = stage === 'adult' || stage === 'elder';
  return {
    ...creature,
    lifecycle: {
      ...creature.lifecycle,
      stage,
      bornAt: nowMs - ageForStage(stage),
      elderSince: stage === 'elder' ? nowMs : null,
      careTier: adult ? creature.lifecycle.careTier : null,
      careFinalScore: adult ? creature.lifecycle.careFinalScore : null,
    },
  };
}

export function applyWorldVisualTestCase(id: string, nowMs = Date.now()): boolean {
  if (import.meta.env.PROD) return false;
  const testCase = WORLD_VISUAL_TEST_CASES.find((candidate) => candidate.id === id);
  const creature = useStore.getState().creature;
  if (!testCase || !creature) return false;
  let next = testCase.stage ? atStage(creature, testCase.stage, nowMs) : { ...creature };
  if (testCase.body) next = { ...next, genome: { ...next.genome, body: testCase.body } };
  visualOverride = {
    facing: testCase.facing,
    locomotion: testCase.locomotion,
    action: testCase.action,
  };
  useStore.setState({ creature: next, playerX: testCase.target.x, playerY: testCase.target.y, inWorld: true });
  return true;
}

function evolution(creature: Creature, tier: CareTier, score: number, nowMs: number): Creature {
  const adult = atStage(creature, 'adult', nowMs);
  return {
    ...adult,
    lifecycle: { ...adult.lifecycle, careTier: tier, careFinalScore: score },
  };
}

export function applyTestStats(creature: Creature, patch: TestStatPatch): Creature {
  const next = { ...creature.state, lastHealAt: null };
  if (patch.hunger !== undefined) next.hunger = clamp(patch.hunger);
  if (patch.happiness !== undefined) next.happiness = clamp(patch.happiness);
  if (patch.weight !== undefined) next.weight = clamp(patch.weight);
  if (patch.discipline !== undefined) next.discipline = clamp(patch.discipline);
  if (patch.vitality !== undefined) next.vitality = clamp(patch.vitality);
  if (patch.poopCount !== undefined) next.poopCount = clamp(patch.poopCount, 0, 5);
  return { ...creature, state: next };
}

export function applyTestPreset(creature: Creature, presetId: TestPresetId, nowMs = Date.now()): Creature {
  const base: Creature = {
    ...creature,
    state: healthyState(creature.state, nowMs),
    lifecycle: { ...creature.lifecycle },
    ...(creature.careProfile ? { careProfile: { ...creature.careProfile } } : {}),
    ...(creature.mutations ? { mutations: creature.mutations.map((mutation) => ({ ...mutation })) } : {}),
  };

  if (presetId.startsWith('stage-')) {
    return atStage(base, presetId.slice(6) as Exclude<Stage, 'dead'>, nowMs);
  }

  switch (presetId) {
    case 'state-perfect':
      return applyTestStats(base, { hunger: 100, happiness: 100, discipline: 100, vitality: 100 });
    case 'state-happy':
      return applyTestStats(base, { happiness: 100 });
    case 'state-sad':
      return applyTestStats(base, { happiness: 5 });
    case 'state-hungry':
      return applyTestStats(base, { hunger: 5 });
    case 'state-dirty':
      return { ...base, state: { ...base.state, poopCount: 3, lastPoopAt: nowMs - 120_000 } };
    case 'state-sick':
      return { ...base, state: { ...base.state, sick: true, sickSince: nowMs - 60_000 } };
    case 'state-tantrum':
      return {
        ...base,
        state: {
          ...base.state,
          happiness: 15,
          tantrum: true,
          tantrumStartedAt: nowMs - 60_000,
          attentionLowSince: nowMs - 120_000,
        },
      };
    case 'state-exhausted':
      return applyTestStats(base, { vitality: 5 });
    case 'state-critical': {
      const critical = applyTestStats(base, { hunger: 2, happiness: 2, vitality: 3, poopCount: 4 });
      return {
        ...critical,
        state: {
          ...critical.state,
          sick: true,
          sickSince: nowMs - 300_000,
          lowHealthSince: nowMs - 300_000,
        },
      };
    }
    case 'flow-breeding':
      return atStage(applyTestStats(base, { hunger: 100, happiness: 100, vitality: 100 }), 'adult', nowMs);
    case 'flow-expedition':
      return atStage(applyTestStats(base, { hunger: 100, happiness: 100, vitality: 100 }), 'adult', nowMs);
    case 'flow-evolution-good':
      return evolution(base, 'bom', 85, nowMs);
    case 'flow-evolution-medium':
      return evolution(base, 'medio', 55, nowMs);
    case 'flow-evolution-bad':
      return evolution(base, 'ruim', 20, nowMs);
    case 'flow-evolution-secret':
      return evolution(base, 'secreto', 95, nowMs);
    case 'flow-memorial':
      return {
        ...base,
        state: { ...base.state, dead: true, causeOfDeath: 'neglect', vitality: 0 },
        lifecycle: { ...base.lifecycle, stage: 'dead' },
      };
    default:
      return base;
  }
}

const SCENARIO_TYPE: Partial<Record<CombatTestScenarioId, AlchemyType>> = {
  'combat-fire': 'Fire', 'combat-water': 'Water', 'combat-earth': 'Earth',
  'combat-air': 'Air', 'combat-life': 'Life', 'combat-shadow': 'Shadow',
};

export function runCombatTestScenario(scenarioId: CombatTestScenarioId): boolean {
  const state = useStore.getState();
  if (!state.creature) return false;
  const service = getCombatRunService();
  if (state.inExpedition) service.extract();
  const prepared = applyTestPreset(state.creature, 'flow-expedition');
  const type = SCENARIO_TYPE[scenarioId] ?? prepared.genome.primaryTypes[0] ?? 'Fire';
  prepared.genome = { ...prepared.genome, primaryTypes: [type] };
  useStore.setState({ creature: prepared, inWorld: true, playerX: 200, playerY: 200 });

  service.start('meadow');
  let seed = `debug-${scenarioId}`;
  if (scenarioId === 'combat-dominant') {
    for (let attempt = 0; attempt < 2_000; attempt++) {
      const candidate = `debug-dominant-${attempt}`;
      if (composeEnemyPopulation({ biome: 'meadow', runId: candidate, count: 6 }).some((spec) => spec.rank === 'dominant')) {
        seed = candidate;
        break;
      }
    }
  }
  service.spawnEnemiesForBiome('meadow', seed);
  const enemies = useStore.getState().enemies;
  const target = scenarioId === 'combat-fauna'
    ? enemies.find((enemy) => enemy.kind === 'fauna')
    : scenarioId === 'combat-dominant'
      ? enemies.find((enemy) => enemy.rank === 'dominant')
      : scenarioId === 'combat-wild'
        ? enemies.find((enemy) => enemy.kind === 'wild')
        : enemies[0];
  if (target) {
    useStore.getState().updateEnemy(target.rid, { x: 260, y: 200, behavior: { kind: 'idle' }, state: 'idle' });
  }
  return true;
}
